import React, { useState, useMemo, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ButtonIcon, { ButtonSizes } from 'src/components/ui/ButtonIcon'
import useTheme from 'src/hooks/useTheme'

export interface MappedTechnology {
  id: string | number
  name: string
  category: string
  ring: string
  description: string
  status: string
}

interface InteractiveRadarProps {
  technologies: MappedTechnology[]
}


export const InteractiveRadar: React.FC<InteractiveRadarProps> = ({ technologies }) => {
  const [selectedTech, setSelectedTech] = useState<MappedTechnology & { angle: number; cx: number; cy: number; indexNumber: string } | null>(null)
  const [rotation, setRotation] = useState(0)
  const scrollCooldown = useRef(false)
  const radarRef = useRef<HTMLDivElement>(null)
  const { theme } = useTheme()
  const isDark = theme === 'dark'







  const RINGS = [
    { name: 'adopt', r: 160, min: 40, max: 140 },
    { name: 'trial', r: 230, min: 180, max: 210 },
    { name: 'assess', r: 300, min: 250, max: 280 },
    { name: 'hold', r: 370, min: 320, max: 350 },
  ]



  // Helper to wrap text into lines
  const wrapText = (text: string, maxChars: number) => {
    const words = text.split(' ')
    const lines: string[] = []
    let currentLine = ''

    words.forEach(word => {
      if ((currentLine + word).length > maxChars) {
        lines.push(currentLine.trim())
        currentLine = word + ' '
      } else {
        currentLine += word + ' '
      }
    })
    lines.push(currentLine.trim())
    return lines.filter(l => l !== '')
  }

  // Helper to wrap category text into lines breaking before ampersand
  const wrapCategoryText = (text: string) => {
    const normalized = text.replace(/&amp;/g, '&')
    if (normalized.includes('&')) {
      const parts = normalized.split('&')
      return [parts[0].trim(), `& ${parts.slice(1).join('&').trim()}`]
    }
    return wrapText(normalized, 15)
  }

  // Grupowanie technologii i przypisywanie im unikalnych kątów (kategorie zajmują własne "sloty")
  const { processedTechnologies, categories } = useMemo(() => {
    const sorted = [...technologies].sort((a, b) => a.category.localeCompare(b.category))
    const uniqueCats = Array.from(new Set(sorted.map(t => t.category)))

    // Liczba slotów to suma technologii i nagłówków kategorii
    const totalSlots = sorted.length + uniqueCats.length
    const angleStep = 360 / Math.max(totalSlots, 1)

    const processedTechs: any[] = []
    const processedCats: any[] = []
    let slotIndex = 0
    let currentCat = ''
    const angleOffset = -angleStep

    sorted.forEach((tech, i) => {
      // Przydzielamy slot na nagłówek kategorii
      if (tech.category !== currentCat) {
        currentCat = tech.category
        processedCats.push({
          name: tech.category,
          lines: wrapCategoryText(tech.category),
          angle: slotIndex * angleStep + angleOffset
        })
        slotIndex++
      }

      // Przydzielamy slot na samą technologię
      const angle = slotIndex * angleStep + angleOffset
      const ringName = tech.ring?.toLowerCase() || ''
      const ring = RINGS.find(r => r.name === ringName) || RINGS[1]

      const rad = (angle - 90) * (Math.PI / 180)
      const cx = 500 + ring.r * Math.cos(rad)
      const cy = 500 + ring.r * Math.sin(rad)

      processedTechs.push({
        ...tech,
        cx,
        cy,
        angle,
        nameLines: wrapText(tech.name, 15),
        indexNumber: String(i + 1).padStart(2, '0')
      })

      slotIndex++
    })

    return { processedTechnologies: processedTechs, categories: processedCats }
  }, [technologies])

  // Nawigacja scrollem w trybie fokusu
  useEffect(() => {
    if (!selectedTech) {
      // @ts-ignore
      if (typeof window !== 'undefined' && window.lenis) {
        // @ts-ignore
        window.lenis.start()
      }
      return
    }

    // Blokujemy scroll strony (Lenis)
    // @ts-ignore
    if (typeof window !== 'undefined' && window.lenis && radarRef.current) {
      // @ts-ignore
      window.lenis.scrollTo(radarRef.current, { offset: -20, immediate: true })
      // @ts-ignore
      window.lenis.stop()
    }

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()

      if (scrollCooldown.current) return
      scrollCooldown.current = true
      setTimeout(() => { scrollCooldown.current = false }, 500)

      const currentIndex = processedTechnologies.findIndex(t => t.id === selectedTech.id)
      if (currentIndex === -1) return

      let nextIndex
      if (e.deltaY > 0) {
        nextIndex = (currentIndex + 1) % processedTechnologies.length
      } else {
        nextIndex = (currentIndex - 1 + processedTechnologies.length) % processedTechnologies.length
      }

      const nextTech = processedTechnologies[nextIndex]
      const targetRotation = -nextTech.angle

      setRotation(prev => {
        const diff = (targetRotation - prev) % 360
        const shortestDiff = ((diff + 540) % 360) - 180
        return prev + shortestDiff
      })

      setSelectedTech(nextTech)
    }

    const targetEl = radarRef.current
    if (targetEl) {
      targetEl.addEventListener('wheel', handleWheel, { passive: false })
    }

    return () => {
      if (targetEl) {
        targetEl.removeEventListener('wheel', handleWheel)
      }
      // Przywracamy scroll strony przy odmontowaniu
      // @ts-ignore
      if (typeof window !== 'undefined' && window.lenis) {
        // @ts-ignore
        window.lenis.start()
      }
    }
  }, [selectedTech, processedTechnologies])

  const handleRotate = (direction: 'left' | 'right') => {
    // Left button rotates clockwise (+45), Right rotates counter-clockwise (-45)
    setRotation(prev => prev + (direction === 'left' ? 45 : -45))
    setSelectedTech(null)
  }

  const handleTechClick = (e: React.MouseEvent, tech: typeof processedTechnologies[0]) => {
    e.stopPropagation()

    // Obliczamy docelową rotację (ujemny kąt technologii, aby znalazła się na górze)
    const targetRotation = -tech.angle

    setRotation(prev => {
      const diff = (targetRotation - prev) % 360
      const shortestDiff = ((diff + 540) % 360) - 180
      return prev + shortestDiff
    })

    setSelectedTech(tech)
  }

  const getTargetY = (ringName: string) => {
    const ring = ringName.toLowerCase()
    if (ring === 'adopt') return 75
    if (ring === 'trial') return 65
    if (ring === 'assess') return 55
    if (ring === 'hold') return 45
    return 50
  }

  // Zoom logic adjusted for cumulative rotation with GPU acceleration
  const getBaseZoomStyle = () => {
    const duration = '1s'
    const easing = 'cubic-bezier(0.19, 1, 0.22, 1)'

    if (!selectedTech) {
      return {
        transform: `translateY(0%) scale(1)`,
        willChange: 'transform',
        transition: `transform ${duration} ${easing}`
      }
    }

    const scale = 2.2
    const ringName = selectedTech.ring.toLowerCase()
    const r = RINGS.find(r => r.name === ringName)?.r || 230
    const targetY = getTargetY(ringName)
    const dY = targetY - 50 + (r / 10) * scale

    return {
      transform: `translateY(${dY}%) scale(${scale})`,
      willChange: 'transform',
      transition: `transform ${duration} ${easing}`
    }
  }

  // Helper to get rotation style with GPU acceleration
  const getRotationStyle = () => {
    return {
      transform: `rotate(${rotation}deg)`,
      transformOrigin: '500px 500px',
      willChange: 'transform',
      transition: 'transform 1s cubic-bezier(0.19, 1, 0.22, 1)'
    }
  }

  return (
    <>
      <div
        ref={radarRef}
        className={`relative w-full flex font-sans rounded-32 ${selectedTech ? 'z-30' : 'z-10'}`}
        style={{ height: '95vh', minHeight: '700px' }}
      >

        {/* Back Button (appears only when zoomed in) */}
        <AnimatePresence>
          {selectedTech && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5, ease: [0.2, 1, 0.3, 1] }}
              className="absolute left-6 md:left-10 top-6 md:top-10 z-50"
            >
              <ButtonIcon
                isOutline={true}
                size={ButtonSizes.large}
                morphingIcon="arrow-to-x"
                onClick={() => setSelectedTech(null)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Rotation Controls - Standard Project Style Outline */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{
            opacity: selectedTech ? 0 : 1,
            x: selectedTech ? -30 : 0
          }}
          transition={{
            duration: selectedTech ? 0.5 : 1.5,
            ease: [0.2, 1, 0.3, 1],
            delay: selectedTech ? 0 : 0.4
          }}
          className={`absolute left-6 md:left-10 top-1/2 -translate-y-1/2 z-20 ${selectedTech ? 'pointer-events-none' : ''}`}
        >
          <ButtonIcon
            isOutline={true}
            size={ButtonSizes.large}
            morphingIcon="rotate-ccw"
            onClick={() => handleRotate('left')}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{
            opacity: selectedTech ? 0 : 1,
            x: selectedTech ? 30 : 0
          }}
          transition={{
            duration: selectedTech ? 0.5 : 1.5,
            ease: [0.2, 1, 0.3, 1],
            delay: selectedTech ? 0 : 0.4
          }}
          className={`absolute right-6 md:right-10 top-1/2 -translate-y-1/2 z-20 ${selectedTech ? 'pointer-events-none' : ''}`}
        >
          <ButtonIcon
            isOutline={true}
            size={ButtonSizes.large}
            morphingIcon="rotate-cw"
            onClick={() => handleRotate('right')}
          />
        </motion.div>

        {/* Radar Canvas - Animates in smoothly */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, ease: [0.2, 1, 0.3, 1], delay: 0.2 }}
          className="flex-1 flex flex-col items-center justify-center p-4 w-full relative z-0 overflow-visible"
          onClick={() => setSelectedTech(null)}
        >

          {/* STATIC LAYER (Labels that don't rotate) */}
          <div className="absolute inset-0 pointer-events-none z-10 flex items-center justify-center p-4">
            <div className="relative h-full aspect-square max-h-[90vh]">
              <svg viewBox="0 0 1000 1000" className="w-full h-full" style={{ overflow: 'visible' }}>
                {/* STATIC LAYER - Ring labels moved to rotating layer to sync with zoom */}
              </svg>
            </div>
          </div>

          {/* ZOOM & PAN LAYER (Scales and moves, but DOES NOT rotate) */}
          <div
            className="relative h-full aspect-square max-h-[90vh] z-0"
            style={getBaseZoomStyle()}
          >
            <svg viewBox="0 0 1000 1000" className="w-full h-full" style={{ overflow: 'visible' }}>
              {/* Ring Labels - They scale and move with the radar, but never rotate */}
              {RINGS.map((ring, idx) => {
                const prevR = idx === 0 ? 0 : RINGS[idx - 1].r
                const centerR = (prevR + ring.r) / 2
                return (
                  <text
                    key={`label-${ring.name}`}
                    x={500 - centerR}
                    y="500"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="currentColor"
                    className={`text-sm font-palanquinDark font-bold uppercase transition-opacity duration-300 ${selectedTech ? 'opacity-0' : 'opacity-100'} text-red/60 dark:text-white/60`}
                  >
                    {ring.name}
                  </text>
                )
              })}

              {/* ROTATION LAYER (Nested inside scaling layer) */}
              <g style={getRotationStyle()}>
                {/* Concentric Rings */}
                {RINGS.map((ring) => (
                  <circle
                    key={ring.name}
                    cx="500"
                    cy="500"
                    r={ring.r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeOpacity={0.2}
                    className="text-red dark:text-white"
                  />
                ))}

                {/* Category Labels and Divider Spokes */}
                {categories.map((cat: any) => {
                  const rad = (cat.angle - 90) * (Math.PI / 180)
                  const x2 = 500 + 370 * Math.cos(rad)
                  const y2 = 500 + 370 * Math.sin(rad)
                  const cx_cat = 500 + 380 * Math.cos(rad)
                  const cy_cat = 500 + 380 * Math.sin(rad)
                  const lines: string[] = cat.lines || [cat.name]

                  return (
                    <g key={cat.name}>
                      {/* Divider Spoke */}
                      <line
                        x1="500" y1="500" x2={x2} y2={y2}
                        stroke="#ED1D23" strokeWidth="2" strokeOpacity={0.6}
                      />

                      <text
                        x={cx_cat}
                        y={cy_cat}
                        textAnchor="start"
                        dominantBaseline="middle"
                        fill="#ED1D23"
                        className="text-base font-black uppercase tracking-[0.3em]"
                        transform={`rotate(${cat.angle - 90}, ${cx_cat}, ${cy_cat})`}
                      >
                        {lines.map((line: string, idx: number) => (
                          <tspan key={idx} x={cx_cat} dy={idx === 0 ? `-${(lines.length - 1) * 0.6}em` : '1.2em'}>
                            {line}
                          </tspan>
                        ))}
                      </text>
                    </g>
                  )
                })}

                {/* Axes / Spokes and Labels */}
                {processedTechnologies.map((tech) => {
                  const rad = (tech.angle - 90) * (Math.PI / 180)
                  const x2 = 500 + 370 * Math.cos(rad)
                  const y2 = 500 + 370 * Math.sin(rad)
                  const cx_label = 500 + 380 * Math.cos(rad)
                  const cy_label = 500 + 380 * Math.sin(rad)
                  const isSelected = selectedTech?.id === tech.id
                  const nameLines: string[] = tech.nameLines || [tech.name]
                  const indent = 24
                  const dyStart = (-(nameLines.length - 1) * 0.6) + 'em'

                  return (
                    <g key={tech.id}>
                      {/* Radial Line (Spoke) */}
                      <line
                        x1="500" y1="500" x2={x2} y2={y2}
                        stroke="currentColor" strokeWidth="1" strokeOpacity={0.2}
                        className="text-red dark:text-white"
                      />

                      {/* Label on the edge - Multi-line and Clickable */}
                      <text
                        x={cx_label}
                        y={cy_label}
                        textAnchor="start"
                        dominantBaseline="central"
                        className={`text-base font-medium transition-colors duration-200 cursor-pointer ${isSelected ? 'text-black dark:text-white' : 'text-zinc-500 dark:text-zinc-400'}`}
                        fill="currentColor"
                        data-selected={isSelected}
                        transform={`rotate(${tech.angle - 90}, ${cx_label}, ${cy_label})`}
                        onClick={(e) => handleTechClick(e, tech)}
                      >
                        <tspan x={cx_label} dy={dyStart} fill="#ED1D23" className="font-bold">
                          {tech.indexNumber}
                        </tspan>
                        <tspan x={cx_label + indent} className={isSelected ? 'text-black dark:text-white' : 'text-zinc-500 dark:text-zinc-400'} fill="currentColor">
                          {nameLines[0]}
                        </tspan>
                        {nameLines.slice(1).map((line: string, idx: number) => (
                          <tspan key={idx} x={cx_label + indent} dy="1.2em" className={isSelected ? 'text-black dark:text-white' : 'text-zinc-500 dark:text-zinc-400'} fill="currentColor">
                            {line}
                          </tspan>
                        ))}
                      </text>

                      {/* Blip */}
                      <g
                        className="cursor-pointer group/blip"
                        onClick={(e) => handleTechClick(e, tech)}
                      >
                        <circle
                          cx={tech.cx} cy={tech.cy} r="11"
                          strokeWidth="1.5"
                          style={{
                            fill: isSelected
                              ? (isDark ? '#FFFFFF' : '#ED1D23')
                              : (isDark ? '#3d3d3dff' : '#101216'),
                            stroke: isSelected
                              ? (isDark ? '#FFFFFF' : '#ED1D23')
                              : 'none',
                            transition: 'fill 0.2s ease-out, stroke 0.2s ease-out'
                          }}
                          className="cursor-pointer group-hover/blip:!fill-[#ED1D23] group-hover/blip:!stroke-[#ED1D23]"
                        />
                        <text
                          x={tech.cx} y={tech.cy + 1}
                          textAnchor="middle" dominantBaseline="middle"
                          fill={isSelected && isDark ? "#000000" : "#FFFFFF"}
                          className="text-base font-mono font-bold pointer-events-none"
                          style={{
                            transformOrigin: `${tech.cx}px ${tech.cy}px`,
                            transform: `rotate(${-rotation}deg)`,
                            fill: isSelected && isDark ? "#000000" : "#FFFFFF"
                          }}
                        >
                          {tech.indexNumber}
                        </text>
                      </g>
                    </g>
                  )
                })}
              </g>
            </svg>
          </div>
        </motion.div>

        {/* Tooltip Dymek - Pozycjonowany dynamicznie przy wybranym punkcie z płynnym blurem i opacity bez ruchu */}
        <AnimatePresence exitBeforeEnter>
          {selectedTech && (
            <motion.div
              key={selectedTech.id}
              initial={{ opacity: 0, filter: 'blur(12px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, filter: 'blur(12px)' }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="absolute left-1/2 z-50 pointer-events-none will-change-[opacity,filter]"
              style={{
                top: `${getTargetY(selectedTech.ring)}%`,
                transform: 'translate(20px, -110%)',
              }}
            >
              <div
                className="bg-white dark:bg-zinc-900 text-black dark:text-white py-6 px-8 rounded-2xl shadow-2xl border border-gray-100 dark:border-white/10 w-[28rem] max-w-[calc(100vw-40px)] relative pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-6 h-6 rounded-full bg-black text-base dark:bg-white text-white dark:text-black flex items-center justify-center font-mono text-base font-bold">
                    {selectedTech.indexNumber}
                  </span>
                  <h3 className="text-xl font-bold tracking-tight">{selectedTech.name}</h3>
                </div>
                <p className="text-xs uppercase tracking-widest text-red font-bold mb-3 whitespace-nowrap">{selectedTech.category} • {selectedTech.ring}</p>
                <p className="text-sm text-gray-500 dark:text-zinc-400 font-semibold leading-relaxed">
                  {selectedTech.description || 'Brak opisu dla tej technologii.'}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}
